/**
 * features.ts — Plan tier definitions and feature matrix.
 *
 * This is the subscription plan policy engine. It defines which features
 * are available at each plan tier, and provides pure functions for
 * plan-based access checks. Works fully offline — no network needed.
 *
 * Architecture:
 *   RBAC (roles.ts)  → "Can this ROLE do this action?"
 *   Plans (features.ts) → "Does this PLAN unlock this feature?"
 *   Combined: Role says you CAN → Plan says you MAY.
 */

// ─── Plan Tiers ──────────────────────────────────────────────────────────────

export const PLAN_TIERS = {
  FREE: 'FREE',
  BASIC: 'BASIC',
  PRO: 'PRO',
  ENTERPRISE: 'ENTERPRISE',
} as const;

export type PlanTier = (typeof PLAN_TIERS)[keyof typeof PLAN_TIERS];

/** Ordered hierarchy — index = rank. Higher index = more features. */
const PLAN_HIERARCHY: readonly PlanTier[] = [
  PLAN_TIERS.FREE,
  PLAN_TIERS.BASIC,
  PLAN_TIERS.PRO,
  PLAN_TIERS.ENTERPRISE,
] as const;

/** Type guard — validates a string is a known PlanTier */
export function isValidPlanTier(value: string): value is PlanTier {
  return Object.values(PLAN_TIERS).includes(value as PlanTier);
}

// ─── Plan Status ─────────────────────────────────────────────────────────────

export const PLAN_STATUSES = {
  ACTIVE: 'ACTIVE',
  GRACE_PERIOD: 'GRACE_PERIOD',
  FROZEN: 'FROZEN',
} as const;

export type PlanStatus = (typeof PLAN_STATUSES)[keyof typeof PLAN_STATUSES];

// ─── Plan Features ───────────────────────────────────────────────────────────

// Staff Management
export const FEATURE_STAFF_MANAGEMENT = 'staff_management';

// Billing — advanced operations
export const FEATURE_CREDIT_NOTES = 'credit_notes';
export const FEATURE_VOID_INVOICES = 'void_invoices';

// Purchasing — PO, suppliers, returns
export const FEATURE_PURCHASING = 'purchasing';

// Customers — statements & ledger
export const FEATURE_CUSTOMER_STATEMENTS = 'customer_statements';

// Reports — GST
export const FEATURE_GST_REPORTS = 'gst_reports';

// Reports — data export
export const FEATURE_DATA_EXPORT = 'data_export';

// Inventory — stock transfer between godowns
export const FEATURE_STOCK_TRANSFER = 'stock_transfer';

// Staff — commission tracking
export const FEATURE_COMMISSION_TRACKING = 'commission_tracking';

// Reports — advanced analytics (batch profitability, etc.)
export const FEATURE_ADVANCED_REPORTS = 'advanced_reports';

// All feature constants collected
export const ALL_PLAN_FEATURES = [
  FEATURE_STAFF_MANAGEMENT,
  FEATURE_CREDIT_NOTES,
  FEATURE_VOID_INVOICES,
  FEATURE_PURCHASING,
  FEATURE_CUSTOMER_STATEMENTS,
  FEATURE_GST_REPORTS,
  FEATURE_DATA_EXPORT,
  FEATURE_STOCK_TRANSFER,
  FEATURE_COMMISSION_TRACKING,
  FEATURE_ADVANCED_REPORTS,
] as const;

export type PlanFeature = (typeof ALL_PLAN_FEATURES)[number];

// ─── Plan → Feature Matrix ──────────────────────────────────────────────────
// FREE: Billing + inventory + dashboard + ALL GST/compliance (GST reports,
//       Tally export, GSTR-2B reconciliation, e-way bill). GST compliance is
//       table-stakes for an Indian POS, so it is available on every tier.
// BASIC: Staff, credit notes, void, purchasing, customer statements.
// PRO: Everything in BASIC + stock transfer, commissions.
// ENTERPRISE: Everything.

const FREE_FEATURES: ReadonlySet<PlanFeature> = new Set<PlanFeature>([
  // GST & compliance are free for everyone.
  FEATURE_GST_REPORTS,
  FEATURE_DATA_EXPORT,
  FEATURE_ADVANCED_REPORTS,
]);

const BASIC_FEATURES: ReadonlySet<PlanFeature> = new Set<PlanFeature>([
  FEATURE_STAFF_MANAGEMENT,
  FEATURE_CREDIT_NOTES,
  FEATURE_VOID_INVOICES,
  FEATURE_PURCHASING,
  FEATURE_CUSTOMER_STATEMENTS,
  FEATURE_GST_REPORTS,
]);

const PRO_FEATURES: ReadonlySet<PlanFeature> = new Set<PlanFeature>([
  // Everything in BASIC
  FEATURE_STAFF_MANAGEMENT,
  FEATURE_CREDIT_NOTES,
  FEATURE_VOID_INVOICES,
  FEATURE_PURCHASING,
  FEATURE_CUSTOMER_STATEMENTS,
  FEATURE_GST_REPORTS,
  // PRO additions
  FEATURE_DATA_EXPORT,
  FEATURE_STOCK_TRANSFER,
  FEATURE_COMMISSION_TRACKING,
  FEATURE_ADVANCED_REPORTS,
]);

const ENTERPRISE_FEATURES: ReadonlySet<PlanFeature> = new Set<PlanFeature>(ALL_PLAN_FEATURES);

/**
 * Master lookup: PlanTier → Set<PlanFeature>.
 */
export const PLAN_FEATURES: Readonly<Record<PlanTier, ReadonlySet<PlanFeature>>> = {
  [PLAN_TIERS.FREE]: FREE_FEATURES,
  [PLAN_TIERS.BASIC]: BASIC_FEATURES,
  [PLAN_TIERS.PRO]: PRO_FEATURES,
  [PLAN_TIERS.ENTERPRISE]: ENTERPRISE_FEATURES,
};

// ─── Feature Display Names (for UI prompts) ─────────────────────────────────

export const FEATURE_DISPLAY_NAMES: Readonly<Record<PlanFeature, string>> = {
  [FEATURE_STAFF_MANAGEMENT]: 'Staff Management',
  [FEATURE_CREDIT_NOTES]: 'Credit Notes',
  [FEATURE_VOID_INVOICES]: 'Void Invoices',
  [FEATURE_PURCHASING]: 'Purchasing & Suppliers',
  [FEATURE_CUSTOMER_STATEMENTS]: 'Customer Statements',
  [FEATURE_GST_REPORTS]: 'GST Reports',
  [FEATURE_DATA_EXPORT]: 'Data Export',
  [FEATURE_STOCK_TRANSFER]: 'Stock Transfer',
  [FEATURE_COMMISSION_TRACKING]: 'Commission Tracking',
  [FEATURE_ADVANCED_REPORTS]: 'Advanced Reports',
};

// ─── Minimum Tier Required (for upgrade prompts) ────────────────────────────

export const FEATURE_MIN_TIER: Readonly<Record<PlanFeature, PlanTier>> = {
  [FEATURE_STAFF_MANAGEMENT]: PLAN_TIERS.BASIC,
  [FEATURE_CREDIT_NOTES]: PLAN_TIERS.BASIC,
  [FEATURE_VOID_INVOICES]: PLAN_TIERS.BASIC,
  [FEATURE_PURCHASING]: PLAN_TIERS.BASIC,
  [FEATURE_CUSTOMER_STATEMENTS]: PLAN_TIERS.BASIC,
  [FEATURE_GST_REPORTS]: PLAN_TIERS.FREE,
  [FEATURE_DATA_EXPORT]: PLAN_TIERS.FREE,
  [FEATURE_STOCK_TRANSFER]: PLAN_TIERS.PRO,
  [FEATURE_COMMISSION_TRACKING]: PLAN_TIERS.PRO,
  [FEATURE_ADVANCED_REPORTS]: PLAN_TIERS.FREE,
};

// ─── Pure Helper Functions ───────────────────────────────────────────────────

/**
 * Check if a plan tier has a specific feature.
 * Returns true for unknown tiers (defensive — never block).
 */
export function planHasFeature(tier: string, feature: PlanFeature): boolean {
  const features = PLAN_FEATURES[tier as PlanTier];
  if (!features) return true; // Unknown tier — allow (never block user)
  return features.has(feature);
}

/**
 * Check if a plan tier is at least the specified minimum tier.
 * Returns true for unknown tiers (defensive).
 */
export function isAtLeastTier(currentTier: string, requiredTier: PlanTier): boolean {
  const currentIndex = PLAN_HIERARCHY.indexOf(currentTier as PlanTier);
  const requiredIndex = PLAN_HIERARCHY.indexOf(requiredTier);
  if (currentIndex === -1) return true; // Unknown tier — allow
  return currentIndex >= requiredIndex;
}

/**
 * Get the rank/index of a plan tier. Higher = more features.
 * Returns 0 (FREE) for unknown tiers.
 */
export function getTierRank(tier: string): number {
  const index = PLAN_HIERARCHY.indexOf(tier as PlanTier);
  return index === -1 ? 0 : index;
}

/**
 * Get the minimum tier required to access a feature.
 * Returns undefined if the feature is not recognized.
 */
export function getMinTierForFeature(feature: PlanFeature): PlanTier | undefined {
  return FEATURE_MIN_TIER[feature];
}
